import { subscribersAPI } from "../services/api";
import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { FaEnvelope, FaHandshake, FaMicroscope, FaBullhorn, FaLocationDot, FaClock } from "react-icons/fa6";
import "./ContactForm.css";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease, delay } },
});

/* ── Contact category cards ─────────────────────────────────── */
const CATEGORIES = [
  {
    Icon: FaEnvelope,
    label: "General",
    desc: "Questions about Forge, our retreats, or general information.",
    email: "info@forge.ngo",
  },
  {
    Icon: FaHandshake,
    label: "Partnerships",
    desc: "Interested in partnering with Forge or bringing us to your community.",
    email: "shane@forge.ngo",
  },
  {
    Icon: FaMicroscope,
    label: "Research",
    desc: "For research inquiries, data requests, or collaborations.",
    email: "josh@forge.ngo",
  },
  {
    Icon: FaBullhorn,
    label: "Media",
    desc: "Press inquiries, story requests, or media partnerships.",
    email: "info@forge.ngo",
  },
];

/* ── Contact info items ─────────────────────────────────────── */
const CONTACT_INFO = [
  {
    Icon: FaLocationDot,
    lines: ["Forge NGO", "5203 Preserve Park Dr", "Spring, TX 77389", "United States"],
    isAddress: true,
  },
  {
    Icon: FaEnvelope,
    lines: ["info@forge.ngo"],
    isEmail: true,
  },
  {
    Icon: FaClock,
    lines: ["MON–FRI  |  9AM – 5PM CST"],
  },
];

/* ── Botanical SVG watermark ─────────────────────────────────── */

/* ── Main component ──────────────────────────────────────────── */
export default function ContactForm() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.email) {
      try { await subscribersAPI.subscribe(form.email, "Contact"); } catch (_) {}
    }
    setSent(true);
    setForm({ name: "", email: "", subject: "", message: "" });
    setTimeout(() => setSent(false), 5000);
  };

  return (
    <section className="ctform" ref={ref}>
      <div className="ctform__inner container-fg container-2xxl">
        {/* ── HOW CAN WE HELP ── */}
        <div className="ctform__help-header">
          <motion.h2 className="ctform__help-title u-eyebrow" variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
            How Can We Help?
          </motion.h2>
          <motion.span className="ctform__help-rule" variants={fadeUp(0.05)} initial="hidden" animate={inView ? "show" : "hidden"} aria-hidden="true" />
        </div>

        {/* Category cards */}
        <div className="ctform__cards">
          {CATEGORIES.map((cat, i) => (
            <motion.div key={cat.label} className="ctform__card" variants={fadeUp(0.06 + i * 0.08)} initial="hidden" animate={inView ? "show" : "hidden"}>
              <div className="ctform__card-icon" aria-hidden="true">
                <cat.Icon />
              </div>
              <h3 className="ctform__card-label">{cat.label.toUpperCase()}</h3>
              <p className="ctform__card-desc">{cat.desc}</p>
              <a href={`mailto:${cat.email}`} className="ctform__card-email">
                {cat.email}
              </a>
            </motion.div>
          ))}
        </div>

        {/* ── LOWER TWO-COLUMN ── */}
        <div className="ctform__lower">
          {/* Left: message form */}
          <motion.div className="ctform__form-col" variants={fadeUp(0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <div className="ctform__section-header">
              <h2 className="ctform__section-title u-eyebrow">Send a Message</h2>
              <span className="ctform__section-rule" aria-hidden="true" />
            </div>

            {sent ? (
              <div className="ctform__success">
                <p>✓ Your message has been sent. We'll be in touch soon.</p>
              </div>
            ) : (
              <form className="ctform__form" onSubmit={handleSubmit} noValidate>
                {/* Name + email row */}
                <div className="ctform__row">
                  <div className="ctform__field">
                    <label htmlFor="ct-name" className="visually-hidden">
                      Full Name
                    </label>
                    <input id="ct-name" name="name" type="text" placeholder="FULL NAME" value={form.name} onChange={handleChange} className="ctform__input" required />
                  </div>
                  <div className="ctform__field">
                    <label htmlFor="ct-email" className="visually-hidden">
                      Email Address
                    </label>
                    <input id="ct-email" name="email" type="email" inputMode="email" placeholder="EMAIL ADDRESS" value={form.email} onChange={handleChange} className="ctform__input" required />
                  </div>
                </div>

                {/* Subject */}
                <div className="ctform__field">
                  <label htmlFor="ct-subject" className="visually-hidden">
                    Subject
                  </label>
                  <input id="ct-subject" name="subject" type="text" placeholder="SUBJECT" value={form.subject} onChange={handleChange} className="ctform__input" required />
                </div>

                {/* Message */}
                <div className="ctform__field">
                  <label htmlFor="ct-message" className="visually-hidden">
                    Your Message
                  </label>
                  <textarea id="ct-message" name="message" placeholder="YOUR MESSAGE" value={form.message} onChange={handleChange} className="ctform__input ctform__textarea" rows={6} required />
                </div>

                <button type="submit" className="ctform__submit">
                  Send Message
                </button>
              </form>
            )}
          </motion.div>

          {/* Right: visit/contact info + botanical */}
          <motion.div className="ctform__info-col" variants={fadeUp(0.16)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <div className="ctform__section-header">
              <h2 className="ctform__section-title u-eyebrow">Visit or Write to Us</h2>
              <span className="ctform__section-rule" aria-hidden="true" />
            </div>

            <div className="ctform__info-list">
              {CONTACT_INFO.map((item, i) => (
                <div key={i} className="ctform__info-item">
                  <span className="ctform__info-icon" aria-hidden="true">
                    <item.Icon />
                  </span>
                  <div className="ctform__info-lines">
                    {item.lines.map((line, j) =>
                      item.isEmail ? (
                        <a key={j} href={`mailto:${line}`} className="ctform__info-link">
                          {line}
                        </a>
                      ) : (
                        <span key={j} className={`ctform__info-line ${j === 0 && item.isAddress ? "ctform__info-line--bold" : ""}`}>
                          {line}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Botanical watermark */}
            <img src="./assets/plant.png" className="ctform__botanical" alt="" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
